'use client';

import React, { useState, useRef, useEffect } from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';

export interface CustomSelectOption {
  value: string;
  label: string;
}

export type CustomSelectVariant = 'default' | 'toolbar';

export interface CustomSelectProps {
  options: CustomSelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** Acessível quando não há `label` visível */
  ariaLabel?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  /** Classe do container do trigger (área clicável) */
  triggerClassName?: string;
  variant?: CustomSelectVariant;
  /** Alinhamento do painel em relação ao gatilho */
  menuAlign?: 'start' | 'end';
  /** Ícone Heroicons à esquerda do texto (ex.: CalendarDaysIcon) */
  leadingIconName?: string;
  disabled?: boolean;
}

const triggerDefault =
  'w-full rounded-lg px-3 py-2.5 text-sm bg-[#fafbfc] border border-border-light text-left text-text-body flex items-center justify-between gap-2 min-h-[40px] focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan transition-colors';

const triggerToolbar =
  'w-full rounded-xl px-3 py-2 text-sm bg-white border border-border-light text-left text-navy-dark font-medium flex items-center justify-between gap-2 min-h-[38px] shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan/30 focus:border-cyan/50 transition-all hover:border-cyan/35 hover:shadow';

const menuDefault = 'left-0 right-0 w-full mt-1';
const menuToolbarStart = 'left-0 right-auto min-w-full mt-1.5';
const menuToolbarEnd = 'left-auto right-0 min-w-full mt-1.5';

const optionDefault = 'px-3 py-2.5 text-sm text-text-body';
const optionToolbar = 'px-3 py-2 text-xs text-navy-dark';

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  label,
  ariaLabel,
  id: idProp,
  placeholder = 'Selecione...',
  className = '',
  triggerClassName = '',
  variant = 'default',
  menuAlign = 'start',
  leadingIconName,
  disabled = false,
}) => {
  const reactId = React.useId();
  const id = idProp ?? `custom-select-${reactId.replace(/:/g, '')}`;
  const labelId = `${id}-label`;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  const triggerClasses =
    variant === 'toolbar' ? `${triggerToolbar} ${triggerClassName}` : `${triggerDefault} ${triggerClassName}`;

  const menuPositionClasses =
    variant === 'toolbar'
      ? menuAlign === 'end'
        ? menuToolbarEnd
        : menuToolbarStart
      : menuDefault;

  const optionRowClasses = variant === 'toolbar' ? optionToolbar : optionDefault;

  const chevronSize = variant === 'toolbar' ? 16 : 18;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {label ? (
        <label id={labelId} htmlFor={id} className="block text-sm font-medium text-navy-dark mb-1">
          {label}
        </label>
      ) : null}
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={!label ? ariaLabel : undefined}
        aria-labelledby={label ? labelId : undefined}
        disabled={disabled}
        className={`${triggerClasses} ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {leadingIconName ? (
            <AppIcon
              name={leadingIconName}
              size={variant === 'toolbar' ? 16 : 18}
              className="shrink-0 text-cyan-dark"
            />
          ) : null}
          <span className="truncate">{displayLabel}</span>
        </span>
        <AppIcon
          name="ChevronDownIcon"
          size={chevronSize}
          className={`shrink-0 text-text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && !disabled ? (
        <ul
          role="listbox"
          className={`absolute z-30 ${menuPositionClasses} rounded-xl border border-border-light bg-white py-1.5 shadow-xl max-h-72 overflow-auto`}
        >
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={value === opt.value}>
              <button
                type="button"
                className={`w-full text-left hover:bg-cyan/10 focus:bg-cyan/10 focus:outline-none focus:ring-0 transition-colors ${optionRowClasses} ${
                  value === opt.value ? 'bg-cyan/15 font-semibold text-cyan-deeper' : ''
                }`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
